import { getSingleParam } from '@confluence/route-manager-utils/entry-points/getSingleParam.ts';

import { PreloadSpacePagesOptions } from '../types.ts';
import {
	SortOrder as CCSortOrder,
	type ConfluenceContentSortField as CCSortBy,
} from '../__types__/SpacePagesQuery.ts';
import {
	SearchSortOrder,
	type SearchSortInput,
	type SearchResultsAGGSearchQueryVariables as SearchResultsAGGSearchQuery$variables,
	SearchConfluenceDocumentStatus,
} from '../__types__/SearchResultsAGGSearchQuery.ts';

export const SPACE_PAGES_QUERY_LIMIT = 12;

export const confluenceEntityAtis = {
	page: 'ati:cloud:confluence:page',
	blogpost: 'ati:cloud:confluence:blogpost',
	embed: 'ati:cloud:confluence:embed',
	folder: 'ati:cloud:confluence:folder',
	whiteboard: 'ati:cloud:confluence:whiteboard',
	database: 'ati:cloud:confluence:database',
};

const AGG_EXPERIENCE = 'ConfSpacePages';
const STARRED_PAGE_LABEL = 'my:favourite';

type GetAggSpacePagesQueryVariablesOptions = Omit<PreloadSpacePagesOptions, 'queryParams'> & {
	isWhiteboardEnabled: boolean;
	isDatabaseEnabled: boolean;
	spaceId: string;
	searchText: string;
	pagesFilter?: string;
	sort?: SearchSortInput[];
	after?: string;
	includeRecommended?: boolean;
};

export const convertCcSortToAggSort = (sortBy?: CCSortBy): SearchSortInput[] => {
	if (!sortBy) {
		return [];
	}
	const order = sortBy.order === CCSortOrder.ASC ? SearchSortOrder.ASC : SearchSortOrder.DESC;
	let field: string;
	switch (sortBy.field) {
		case 'LAST_MODIFIED_DATE':
			field = 'latestModifiedDate';
			break;
		case 'TITLE':
			field = 'title';
			break;
		default:
			field = 'latestModifiedDate';
	}
	return [
		{
			field,
			order,
		},
	];
};

export const getAggSpacePagesQueryVariables = ({
	cloudId,
	spaceKey,
	userId,
	isLicensed,
	spaceId,
	isWhiteboardEnabled,
	isDatabaseEnabled,
	searchText,
	pagesFilter,
	sort,
	after = '',
	includeRecommended = false,
}: GetAggSpacePagesQueryVariablesOptions): SearchResultsAGGSearchQuery$variables => {
	return {
		cloudIdARI: `ari:cloud:confluence::site/${cloudId}`,
		tenantId: cloudId,
		limit: SPACE_PAGES_QUERY_LIMIT,
		after,
		query: searchText,
		commonFilters: {
			range: {},
		},
		confluenceFilters: {
			spacesFilter: [spaceKey],
			...(pagesFilter === 'worked-on' &&
				userId &&
				isLicensed && {
					contributorsFilter: [userId],
				}),
			...(pagesFilter === 'starred' &&
				isLicensed && {
					labelsFilter: [STARRED_PAGE_LABEL],
				}),
			titleMatchOnly: true,
			contentStatuses: [
				SearchConfluenceDocumentStatus.CURRENT,
				SearchConfluenceDocumentStatus.DRAFT,
			],
			containerStatus: [],
		},
		sort: sort ? sort : [],
		entities: [
			confluenceEntityAtis.page,
			confluenceEntityAtis.embed,
			confluenceEntityAtis.folder,
			...(isWhiteboardEnabled ? [confluenceEntityAtis.whiteboard] : []),
			...(isDatabaseEnabled ? [confluenceEntityAtis.database] : []),
		],
		experience: AGG_EXPERIENCE,
		spaceId,
		isLicensed: Boolean(isLicensed),
		includeRecommended,
	};
};

export const getPreloadAggSpacePagesQueryVariables = (
	options: PreloadSpacePagesOptions,
): SearchResultsAGGSearchQuery$variables => {
	const {
		cloudId,
		spaceKey,
		queryParams,
		userId,
		isLicensed,
		spaceId = '',
		isWhiteboardEnabled = false,
		isDatabaseEnabled = false,
	} = options;

	const pagesFilter = getSingleParam(queryParams, 'pagesFilter');
	const searchText = getSingleParam(queryParams, 'text') || '';

	const sort = [
		{
			field: 'latestModifiedDate',
			order: SearchSortOrder.DESC,
		},
	];

	return getAggSpacePagesQueryVariables({
		cloudId,
		spaceKey,
		userId,
		isLicensed,
		spaceId,
		isWhiteboardEnabled,
		isDatabaseEnabled,
		searchText,
		pagesFilter,
		sort,
	});
};
